import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { formatCurrency } from "../intl_helpers";
import type { Account, ErrorWithJSON, Invoice } from "../types";
import { route } from "../urls";
import GlyphLoader from "./glyph-loader";
import IconTooltip from "./icon-tooltip";

export default function Invoices({
  account,
}: {
  account: Account;
}) {
  const query = useInvoicesQuery(account);

  if (query.isError) {
    return (
      <p className="text-r8-red-10">
        Sorry, we were unable to load your invoices.
      </p>
    );
  }

  if (query.isPending) {
    return (
      <div className="flex items-center gap-2">
        <GlyphLoader />
        <p>Loading invoices...</p>
      </div>
    );
  }

  if (query.data.invoices.length === 0) {
    return <p>No invoices found.</p>;
  }

  const monthlyUsageInvoices = query.data.invoices.filter(
    (invoice) => invoice.type === "monthly-usage"
  );
  const creditPurchaseInvoices = query.data.invoices.filter(
    (invoice) => invoice.type === "credit-purchase"
  );

  const monthlyUsageMonthFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
  });
  const creditPurchaseDateFormatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
  });

  return (
    <div className="space-y-8">
      {monthlyUsageInvoices.length > 0 ? (
        <div>
          {creditPurchaseInvoices.length > 0 ? <h4>Monthly usage</h4> : null}
          <table className="table-fixed w-full text-sm">
            <thead>
              <tr className="border-b border-r8-gray-5 text-left text-base text-r8-gray-11 font-normal">
                <th className="p-2 w-1/2 font-normal">Month</th>
                <th className="p-2 w-1/4 font-normal">Status</th>
                <th className="p-2 w-1/4 font-normal">
                  <div className="inline-flex items-center gap-1.5">
                    <span>Total</span>
                    <IconTooltip
                      icon="Info"
                      weight="bold"
                      tooltipText="Total usage before any credit or adjustments"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {monthlyUsageInvoices.map((invoice) => {
                const invoiceUrl = query.data.invoice_urls[invoice.id];
                const invoiceMonth = monthlyUsageMonthFormatter.format(
                  new Date(invoice.started_on)
                );

                return (
                  <tr
                    key={invoice.id}
                    className="[&:not(:last-child)]:border-b border-r8-gray-5"
                  >
                    <td className="p-2 w-1/2">
                      {invoiceUrl ? (
                        <a href={invoiceUrl}>{invoiceMonth}</a>
                      ) : (
                        invoiceMonth
                      )}
                    </td>
                    <td className="p-2 w-1/4">
                      {formatMonthlyUsageInvoiceStatus(invoice.status)}
                    </td>
                    <td className="p-2 w-1/4">
                      {formatCurrency(invoice.total_cost_before_adjustments)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
      {creditPurchaseInvoices.length > 0 ? (
        <div>
          {monthlyUsageInvoices.length > 0 ? <h4>Credit purchases</h4> : null}
          <table className="table-fixed w-full text-sm">
            <thead>
              <tr className="border-b border-r8-gray-5 text-left text-base text-r8-gray-11 font-normal">
                <th className="p-2 w-1/2 font-normal">Date</th>
                <th className="p-2 w-1/4 font-normal">Status</th>
                <th className="p-2 w-1/4 font-normal">Total</th>
              </tr>
            </thead>
            <tbody>
              {creditPurchaseInvoices.map((invoice) => {
                const invoiceUrl = query.data.invoice_urls[invoice.id];
                const invoiceDate = creditPurchaseDateFormatter.format(
                  new Date(invoice.started_on)
                );

                return (
                  <tr
                    key={invoice.id}
                    className="[&:not(:last-child)]:border-b border-r8-gray-5"
                  >
                    <td className="p-2 w-1/2">
                      {invoiceUrl ? (
                        <a href={invoiceUrl}>{invoiceDate}</a>
                      ) : (
                        invoiceDate
                      )}
                    </td>
                    <td className="p-2 w-1/4">
                      {formatCreditPurchaseInvoiceStatus(invoice.status)}
                    </td>
                    <td className="p-2 w-1/4">
                      {formatCurrency(invoice.total_cost)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export function formatCreditPurchaseInvoiceStatus(status: string) {
  switch (status) {
    case "DRAFT":
      return "In progress";
    case "FINALIZED":
      return "Succeeded";
    case "VOID":
      return "Failed";
    default:
      return status;
  }
}

export function formatMonthlyUsageInvoiceStatus(status: string) {
  switch (status) {
    case "DRAFT":
      return "In progress";
    case "FINALIZED":
      return "Issued";
    default:
      return status;
  }
}

export interface InvoicesResponse {
  invoices: Invoice[];
  invoice_urls: Record<Invoice["id"], string>;
}

export function useInvoicesQuery(
  account: Account
): UseQueryResult<InvoicesResponse, ErrorWithJSON> {
  return useQuery({
    queryKey: ["invoices", account.kind, account.username],
    queryFn: () => fetchInvoices(account),
    refetchOnWindowFocus: false,
  });
}

async function fetchInvoices(account: Account): Promise<InvoicesResponse> {
  const res = await fetch(
    account.kind === "organization"
      ? route("api_organization_list_invoices", {
          organization_name: account.username,
        })
      : route("api_user_list_invoices", {
          username: account.username,
        })
  );

  if (res.ok) {
    return res.json<InvoicesResponse>();
  }

  try {
    const errorJson = await res.json<ErrorWithJSON>();
    return Promise.reject({
      ...errorJson,
      status: res.status,
    });
  } catch (e) {
    return Promise.reject({
      detail: "Failed to fetch invoices",
      status: res.status,
    });
  }
}

export interface UnusedCreditResponse {
  link_to_add_credit: string | null;
  unused_credit: string;
}

export function useUnusedCreditQuery(
  account: Account
): UseQueryResult<UnusedCreditResponse, ErrorWithJSON> {
  return useQuery({
    queryKey: ["unused_credit", account.kind, account.username],
    queryFn: () => fetchUnusedCredit(account),
    refetchOnWindowFocus: false,
  });
}

async function fetchUnusedCredit(
  account: Account
): Promise<UnusedCreditResponse> {
  const res = await fetch(
    account.kind === "organization"
      ? route("api_organization_get_unused_credit", {
          organization_name: account.username,
        })
      : route("api_user_get_unused_credit", {
          username: account.username,
        })
  );

  if (res.ok) {
    return res.json<UnusedCreditResponse>();
  }

  try {
    const errorJson = await res.json<ErrorWithJSON>();
    return Promise.reject({
      ...errorJson,
      status: res.status,
    });
  } catch (e) {
    return Promise.reject({
      detail: "Failed to fetch unused credit",
      status: res.status,
    });
  }
}
